import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  HostBinding,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LogoComponent } from '../../components/logo/logo.component';
import { NavigationService } from '../../services/navigation.service';
import { ProfileService } from '../../services/profile.service';
import { AuthService } from '../../services/auth.service';
import { ApiService } from '../../services/api.service';
import { RoleService } from '../../services/role.service';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { UserProfile } from '../types/user.interface';
import { UserRole } from '../types/role.interface';
import { Subscription } from 'rxjs';

/**
 * Side navigation component providing a collapsible, role-aware sidebar that displays the current user's profile, avatar/initials, and context-sensitive navigation links with mobile support and collapsed-state tooltips.
 * @component
 * @selector app-side-nav
 * @example
 *   <app-side-nav></app-side-nav>
 *
 * Description:
 *   - Purpose: Renders the application's side navigation UI, shows user profile information, manages collapse/expand and mobile navigation state, and adapts visible links and behavior based on the current user role and permissions.
 *   - Data sources and coordination: Uses ProfileService to load and subscribe to user profile changes, RoleService to manage and query roles/permissions, NavigationService to coordinate sidebar and mobile nav state, and AuthService/ApiService for authentication and initial role resolution.
 *
 * Key methods:
 *   - toggleCollapse(): Toggles the sidebar collapsed state and persists it via NavigationService.
 *   - onDocumentClick(event): Host listener that will auto-collapse the sidebar when running in campaign editor mode and a click occurs outside the sidebar.
 *   - onEditProfile(): Placeholder handler to initiate profile editing (currently logs to console).
 *   - onLogout(): Delegates logout to AuthService.
 *   - closeMobileNav(): Closes the mobile navigation via NavigationService.
 *   - loadUserProfile(), subscribeToProfileChanges(), subscribeToRoleChanges(): Manage loading and subscriptions for profile and role state to keep the UI in sync.
 *   - getProfileImage(), userName, userRole, initials, hasProfileImage: Helpers for deriving displayable profile data and fallbacks.
 *   - isAdmin, isStaff, hasPermission(permission): Role/permission helper accessors that forward checks to RoleService.
 *   - updateTooltipPosition(menuLink) and setupTooltipScrollHandler(): Position collapsed-state tooltips on hover and update their position on scroll.
 *
 * Lifecycle hooks:
 *   - ngOnInit(): Subscribes to profile and role observables, initializes role from AuthService if needed, checks campaign-editor route to set default collapsed state, and sets up tooltip scroll handling.
 *   - ngOnDestroy(): Unsubscribes aggregated subscriptions to prevent memory leaks.
 *
 * Usage notes:
 *   - The component communicates primarily via injected services and does not expose public inputs or outputs. Route inspection is used for campaign-editor mode detection.
 *   - Tooltip DOM calculations rely on specific markup/classes (e.g., .sidenav-container, .menu-tooltip, .menu-link) present in the component template.
 *
 * @input none - No public @Input properties; state and configuration are provided via injected services and application routing.
 * @output none - No @Output events; interactions and state changes are propagated through NavigationService and service observables instead.
 */
@Component({
  selector: 'app-side-nav',
  standalone: true,
  imports: [CommonModule, LogoComponent, RouterLink, RouterLinkActive],
  templateUrl: './side-nav.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./side-nav.component.scss'],
})
export class SideNavComponent implements OnInit, OnDestroy {
  isCollapsed = false;
  isMobileNavOpen = false;
  userProfile: UserProfile | null = null;
  currentUserRole: UserRole | null = null;
  private subscriptions: Subscription = new Subscription();
  private isCampaignEditorMode = false;

  /** Desktop icon-rail collapse only — portrait hamburger drawer is always expanded. */
  get isNavCollapsed(): boolean {
    return this.isCollapsed && !this.isMobileNavOpen;
  }

  @HostBinding('class.sidebar-collapsed')
  get sidebarCollapsedHostClass(): boolean {
    return this.isNavCollapsed;
  }

  /**
   * Initializes the SideNavComponent, wiring services and subscribing to mobile navigation state changes.
   * @example
   * new SideNavComponent(navigationService, profileService, authService, apiService, roleService)
   * Instance of SideNavComponent with mobile nav subscription registered.
   * @param {{NavigationService}} {{navigationService}} - Service that exposes the isMobileNavOpen$ observable for mobile navigation state.
   * @param {{ProfileService}} {{profileService}} - Service used to access user profile information.
   * @param {{AuthService}} {{authService}} - Service used to access authentication and user session functions.
   * @param {{ApiService}} {{apiService}} - Service used to make API requests.
   * @param {{RoleService}} {{roleService}} - Service used to check or manage user roles and permissions.
   * @returns {{void}} Constructor does not return a value.
   **/
  constructor(
    private navigationService: NavigationService,
    private profileService: ProfileService,
    private authService: AuthService,
    private apiService: ApiService,
    private roleService: RoleService
  ) {
    this.subscriptions.add(
      this.navigationService.isMobileNavOpen$.subscribe(isOpen => {
        this.isMobileNavOpen = isOpen;
      })
    );
    this.subscriptions.add(
      this.navigationService.isSidebarCollapsed$.subscribe(collapsed => {
        this.isCollapsed = collapsed;
      })
    );
  }

  /**
   * Initialize the side navigation component: subscribes to profile and role changes, loads the user profile, initializes role from the auth service if available, checks for campaign editor mode, and sets up a scroll handler to update tooltip positions.
   * @example
   * gOnInit()
   * void
   * @returns {void} Does not return a value.
   **/
  ngOnInit(): void {
    this.navigationService.restoreSidebarCollapsedForCurrentUser();
    this.subscribeToProfileChanges();
    this.subscribeToRoleChanges();
    this.loadUserProfile();

    // Ensure role is set from auth service if available
    this.initializeRoleFromAuth();

    // Check if we're in campaign editor mode
    this.checkCampaignEditorMode();

    // Add scroll listener to update tooltip positions
    this.setupTooltipScrollHandler();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  subscribeToProfileChanges(): void {
    this.subscriptions.add(
      this.profileService.userProfile$.subscribe(profile => {
        this.userProfile = profile;
        // Set role when profile is loaded
        if (profile?.role) {
          this.roleService.setUserRole(profile.role as any);
        }
      })
    );
  }

  subscribeToRoleChanges(): void {
    this.subscriptions.add(
      this.roleService.currentUserRole$.subscribe((role: UserRole | null) => {
        this.currentUserRole = role;
      })
    );
  }

  loadUserProfile(): void {
    // Use ProfileService to load profile if needed (prevents duplicate API calls)
    this.profileService.loadUserProfileIfNeeded();

    // Set role when profile is loaded (will be handled by subscription, but also check current value)
    const currentProfile = this.profileService.getCurrentUserProfile();
    if (currentProfile?.role) {
      this.roleService.setUserRole(currentProfile.role as any);
    }
  }

  toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.navigationService.setSidebarCollapsed(this.isCollapsed);
  }

  /**
   * Collapse the side navigation when a click occurs outside the sidenav while in campaign editor mode.
   * @example
   * onDocumentClick(event)
   * undefined
   * @param {{Event}} {{event}} - Document click event containing the target element.
   * @returns {{void}} No return value.
   **/
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Only apply click-outside-to-collapse behavior in campaign editor mode
    if (this.isCampaignEditorMode && !this.isCollapsed) {
      const target = event.target as HTMLElement;
      const sidenavElement = document.querySelector('.sidenav-container');

      // Check if click is outside the sidenav
      if (sidenavElement && !sidenavElement.contains(target)) {
        this.isCollapsed = true;
        this.navigationService.setSidebarCollapsed(this.isCollapsed, { persist: false });
      }
    }
  }

  onEditProfile() {
    // TODO: Implement edit profile functionality
    console.log('Edit profile clicked');
  }

  onLogout() {
    this.closeMobileNav();
    this.authService.logout();
  }

  closeMobileNav() {
    this.navigationService.setMobileNavOpen(false);
  }

  /** Navigate via sidebar icon/text without expanding a collapsed desktop rail. */
  onMenuLinkClick(): void {
    this.closeMobileNav();
  }

  getProfileImage(): string {
    if (this.userProfile?.picture_url) {
      return this.userProfile.picture_url;
    }
    return 'assets/images/avatar.png';
  }

  get userName(): string {
    return this.userProfile?.name || '';
  }

  get userRole(): string {
    return this.userProfile?.role || 'User';
  }

  get initials(): string {
    const name = this.userProfile?.name;
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  get hasProfileImage(): boolean {
    return Boolean(this.userProfile?.picture_url);
  }

  // Role-based permission methods
  get isAdmin(): boolean {
    return this.roleService.isAdmin();
  }

  /** Operational shop-floor menu (attendant and attendant plus). */
  get isAttendant(): boolean {
    return this.roleService.isShopFloorEmployee();
  }

  /** Attendance-only staff menu. */
  get isStaff(): boolean {
    return this.roleService.isStaff();
  }

  hasPermission(permission: string): boolean {
    return this.roleService.hasPermission(permission as any);
  }

  get dashboardRoute(): string {
    return this.isAttendant || this.isStaff ? '/attendance' : '/dashboard';
  }

  /**
   * Initialize the current user's role from the authentication service or default to ADMIN.
   * @example
   * initializeRoleFromAuth()
   * void
   * @param {void} none - This function does not accept any parameters.
   * @returns {void} No return value; sets the user's role in the RoleService based on auth data or defaults to UserRole.ADMIN.
   **/
  private initializeRoleFromAuth(): void {
    // Check if role is already set
    if (this.roleService.getCurrentUserRole()) {
      return;
    }

    // Try to get role from auth service user data
    const userData = this.authService.user();
    if (userData?.role) {
      this.roleService.setUserRole(userData.role as any);
    } else {
      // Default to admin if no role is found
      this.roleService.setUserRole(UserRole.ADMIN);
    }
  }

  private checkCampaignEditorMode(): void {
    // Check if the current route is the campaign-editor route
    const currentUrl = window.location.pathname;
    this.isCampaignEditorMode =
      currentUrl === '/campaign-editor' || currentUrl.includes('/campaign-editor');

    // If we're in campaign editor mode, set default collapsed state to true
    if (this.isCampaignEditorMode) {
      this.isCollapsed = true;
      this.navigationService.setSidebarCollapsed(this.isCollapsed, { persist: false });
    }
  }

  onMenuLinkMouseEnter(event: MouseEvent): void {
    if (!this.isNavCollapsed) return;
    this.updateTooltipPosition(event.currentTarget as HTMLElement);
  }

  /**
   * Update the tooltip's position so it appears to the right of the sidenav and vertically centered relative to the menu link.
   * @example
   * updateTooltipPosition(menuLink)
   * undefined
   * @param {{HTMLElement}} {{menuLink}} - The menu link element whose tooltip will be positioned.
   * @returns {{void}} Nothing; updates the tooltip element's style to position it adjacent to the sidenav.
   **/
  private updateTooltipPosition(menuLink: HTMLElement): void {
    const tooltip = menuLink.querySelector('.menu-tooltip') as HTMLElement;

    if (tooltip) {
      const rect = menuLink.getBoundingClientRect();
      const sidenavRect = (
        menuLink.closest('.sidenav-container') as HTMLElement
      )?.getBoundingClientRect();

      if (sidenavRect) {
        // Position tooltip to the right of the sidenav container
        tooltip.style.left = `${sidenavRect.right + 12}px`;
        tooltip.style.top = `${rect.top + rect.height / 2}px`;
        tooltip.style.transform = 'translateY(-50%)';
      }
    }
  }

  /**
   * Attach a scroll listener to the .sidenav-container that updates the tooltip position for the currently hovered menu link when the side-nav is collapsed.
   * @example
   * setupTooltipScrollHandler()
   * // void
   * @returns {void} Does not return a value.
   */
  private setupTooltipScrollHandler(): void {
    // Update tooltip positions on scroll when sidebar is collapsed
    const sidenavContainer = document.querySelector('.sidenav-container');
    if (sidenavContainer) {
      sidenavContainer.addEventListener('scroll', () => {
        if (this.isCollapsed) {
          const hoveredLink = document.querySelector('.menu-link:hover') as HTMLElement;
          if (hoveredLink) {
            this.updateTooltipPosition(hoveredLink);
          }
        }
      });
    }
  }
}
